import React, { useRef, useEffect, useCallback, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { RS_GEOJSON } from '../../data/rsGeoJson';
import { RS_MESORREGIAO_GEOJSON } from '../../data/rsMesorregiaoGeoJson';
import citiesWithCoordinates from '../../data/cities_with_coordinates.json';

interface DemandsHeatMapProps {
  demandCounts: Record<number, { 
    total: number; 
    naoIniciada: number; 
    emAndamento: number; 
    concluida: number; 
    naoAtribuida: number;
  }>;
  loading?: boolean;
  onCityClick?: (cityId: number, cityName: string) => void;
  onVisibleMaxChange?: (max: number) => void;
}

// ----- Styled components -----

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.6; }
  70%  { transform: scale(1.9); opacity: 0; }
  100% { transform: scale(1.9); opacity: 0; }
`;

const RecenterButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 160px;
  right: 30px;
  z-index: 30;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: white;
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.10);
  opacity: ${p => p.$visible ? 1 : 0};
  pointer-events: ${p => p.$visible ? 'auto' : 'none'};
  transform: ${p => p.$visible ? 'scale(1)' : 'scale(0.8)'};
  transition: opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  /* Pulse ring */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid var(--accent);
    opacity: ${p => p.$visible ? 0.6 : 0};
    animation: ${p => p.$visible ? css`${pulseRing} 2s ease-out infinite` : 'none'};
  }

  svg { width: 26px; height: 26px; }

  &:hover {
    box-shadow: 0 4px 14px rgba(59,130,246,0.25);
    background: #eff6ff;
  }
  &:active { transform: scale(0.95); }
`;

const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
  cursor: default;
  touch-action: none;
`;

const LegendContainer = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  min-width: 170px;
  pointer-events: none;
`;

const LegendTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 10px;
`;

const LegendGradient = styled.div`
  height: 10px;
  border-radius: 5px;
  background: linear-gradient(to right, var(--accent-dark), #06b6d4, #22c55e, #eab308, #f97316, #ef4444);
  margin-bottom: 6px;
`;

const LegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LegendLabel = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
`;

const TooltipEl = styled.div<{ $x: number; $y: number; $visible: boolean }>`
  position: absolute;
  left: ${p => p.$x}px;
  top: ${p => p.$y}px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: ${p => p.$visible ? 1 : 0};
  transition: opacity 0.15s ease;
  white-space: nowrap;
  z-index: 100;
`;

const TooltipCityName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
`;

const TooltipCount = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

// ----- Thermal color scale -----
// stops: deep-blue → cyan → green → yellow → orange → red
const HEAT_STOPS: [number, number, number][] = [
  [37,  99,  235],  // 0.00 – deep blue  (var(--accent-dark))
  [6,   182, 212],  // 0.20 – cyan       (#06b6d4)
  [34,  197, 94 ],  // 0.40 – green      (#22c55e)
  [234, 179, 8  ],  // 0.60 – yellow     (#eab308)
  [249, 115, 22 ],  // 0.80 – orange     (#f97316)
  [239, 68,  68 ],  // 1.00 – red        (#ef4444)
];

function thermalColor(t: number, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const seg = clamped * (HEAT_STOPS.length - 1);
  const i   = Math.floor(seg);
  const f   = seg - i;
  const c1  = HEAT_STOPS[Math.min(i, HEAT_STOPS.length - 1)];
  const c2  = HEAT_STOPS[Math.min(i + 1, HEAT_STOPS.length - 1)];
  const r = Math.round(c1[0] + (c2[0] - c1[0]) * f);
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * f);
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * f);
  return `rgba(${r},${g},${b},${alpha.toFixed(2)})`;
}

// Glow color from same scale (more transparent)
function thermalGlowColor(t: number): string {
  return thermalColor(t, t * 0.3);
}

const RS_CITIES = (citiesWithCoordinates as any[]).filter(
  c => c.microrregiao?.mesorregiao?.UF?.sigla === 'RS'
);

// ----- Component -----

interface ViewTransform {
  scale: number;
  offsetX: number;
  offsetY: number;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

/** Clamp pan offset so the map never moves more than 40% of the canvas size beyond center at current scale. */
function clampOffset(offsetX: number, offsetY: number, scale: number, W: number, H: number) {
  const maxX = (W / 2) * (scale - 1) + W * 0.15;
  const maxY = (H / 2) * (scale - 1) + H * 0.15;
  return {
    offsetX: Math.max(-maxX, Math.min(maxX, offsetX)),
    offsetY: Math.max(-maxY, Math.min(maxY, offsetY)),
  };
}

const DemandsHeatMap: React.FC<DemandsHeatMapProps> = ({
  demandCounts,
  loading = false,
  onCityClick,
  onVisibleMaxChange,
}) => {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Zoom / pan state stored in a ref for perf (no re-render on every frame)
  const viewRef = useRef<ViewTransform>({ scale: 1, offsetX: 0, offsetY: 0 });
  const dragRef = useRef<{ active: boolean; lastX: number; lastY: number }>({
    active: false, lastX: 0, lastY: 0,
  });

  const [tooltip, setTooltip] = useState<{
    x: number; 
    y: number; 
    visible: boolean; 
    cityName: string; 
    data: { 
      total: number; 
      naoIniciada: number; 
      emAndamento: number; 
      concluida: number; 
      naoAtribuida: number;
    } | null;
  }>({ x: 0, y: 0, visible: false, cityName: '', data: null });

  const [isZoomed, setIsZoomed] = useState(false);

  // Track visible-max to normalise heat per zoom level
  const visibleMaxRef = useRef<number>(1);

  // ── Geo base projection (maps geo coords → base canvas coords at scale=1) ──
  // Use same fixed bounds as CanvasMap for consistent RS positioning
  const FIXED_BOUNDS = {
    north: -27.0,
    south: -33.8,
    east: -49.5,
    west: -57.7
  };

  /** Convert geo → base canvas coordinates (before view transform). */
  const geoToBase = useCallback((
    lng: number, lat: number, W: number, H: number
  ) => {
    // Match CanvasMap's padding strategy: extra 200px on left
    const leftPadding = 40 + 200;
    const rightPadding = 40;
    const topPadding = 40;
    const bottomPadding = 40;
    
    const effectiveWidth = W - leftPadding - rightPadding;
    const effectiveHeight = H - topPadding - bottomPadding;
    
    const geoW = FIXED_BOUNDS.east - FIXED_BOUNDS.west;
    const geoH = FIXED_BOUNDS.north - FIXED_BOUNDS.south;
    
    const scaleX = effectiveWidth / geoW;
    const scaleY = effectiveHeight / geoH;
    const scale = Math.min(scaleX, scaleY);
    
    let offsetX = leftPadding;
    let offsetY = topPadding;
    
    if (scaleX < scaleY) {
      // Width constrained, center vertically
      offsetY = topPadding + (effectiveHeight - geoH * scale) / 2;
    } else {
      // Height constrained, center horizontally
      offsetX = leftPadding + (effectiveWidth - geoW * scale) / 2;
    }
    
    return {
      x: offsetX + (lng - FIXED_BOUNDS.west) * scale,
      y: offsetY + (FIXED_BOUNDS.north - lat) * scale,
    };
  }, []);

  /** Apply current view transform (scale + pan) on top of base coords. */
  const applyView = useCallback((bx: number, by: number, W: number, H: number) => {
    const { scale, offsetX, offsetY } = viewRef.current;
    const cx = W / 2, cy = H / 2;
    return {
      x: cx + (bx - cx) * scale + offsetX,
      y: cy + (by - cy) * scale + offsetY,
    };
  }, []);

  const geoToCanvas = useCallback((lng: number, lat: number, W: number, H: number) => {
    const base = geoToBase(lng, lat, W, H);
    return applyView(base.x, base.y, W, H);
  }, [geoToBase, applyView]);

  // ── Draw ────────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = wrapper.getBoundingClientRect();
    canvas.width  = rect.width  * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width  = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.scale(dpr, dpr);

    const W = rect.width;
    const H = rect.height;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // ── State clip path ──────────────────────────────────────────────────────
    const stateFeature = RS_GEOJSON.features[0];
    let statePath: Path2D | null = null;
    if (stateFeature?.geometry?.type === 'Polygon') {
      statePath = new Path2D();
      (stateFeature.geometry.coordinates[0] as [number, number][]).forEach(([lng, lat], i) => {
        const { x, y } = geoToCanvas(lng, lat, W, H);
        i === 0 ? statePath!.moveTo(x, y) : statePath!.lineTo(x, y);
      });
      statePath.closePath();
    }

    if (statePath) {
      ctx.save();
      ctx.clip(statePath);
      ctx.fillStyle = 'rgba(241, 245, 249, 1)';
      ctx.fillRect(0, 0, W, H);

      // Mesorregião borders
      RS_MESORREGIAO_GEOJSON.features.forEach((feature: any) => {
        const drawRing = (coords: [number, number][]) => {
          if (!coords.length) return;
          const first = geoToCanvas(coords[0][0], coords[0][1], W, H);
          ctx.moveTo(first.x, first.y);
          for (let i = 1; i < coords.length; i++) {
            const { x, y } = geoToCanvas(coords[i][0], coords[i][1], W, H);
            ctx.lineTo(x, y);
          }
        };
        ctx.beginPath();
        if (feature.geometry.type === 'Polygon') drawRing(feature.geometry.coordinates[0]);
        else if (feature.geometry.type === 'MultiPolygon')
          feature.geometry.coordinates.forEach((poly: [number, number][][]) => drawRing(poly[0]));
        ctx.closePath();
        ctx.strokeStyle = 'rgba(203, 213, 225, 0.8)';
        ctx.lineWidth = 0.8 / viewRef.current.scale;
        ctx.stroke();
      });

      ctx.restore();
    }



    // ── Compute visible-area max (for adaptive heat scale) ──────────────────
    let visibleMax = 1;
    RS_CITIES.forEach((city: any) => {
      if (!city.latitude || !city.longitude) return;
      const { x, y } = geoToCanvas(city.longitude, city.latitude, W, H);
      if (x < -20 || x > W + 20 || y < -20 || y > H + 20) return;
      const cityData = demandCounts[city.id];
      const cnt = cityData?.total ?? 0;
      if (cnt > visibleMax) visibleMax = cnt;
    });
    visibleMaxRef.current = visibleMax;
    onVisibleMaxChange?.(visibleMax);

    const hasData = Object.keys(demandCounts).length > 0;
    const zs = viewRef.current.scale;

    // ── City dots ────────────────────────────────────────────────────────────
    RS_CITIES.forEach((city: any) => {
      if (!city.latitude || !city.longitude) return;
      const { x, y } = geoToCanvas(city.longitude, city.latitude, W, H);
      // Skip cities off screen (with small margin)
      if (x < -20 || x > W + 20 || y < -20 || y > H + 20) return;

      const cityData = demandCounts[city.id];

      if (!hasData || !cityData) {
        // No demands: small solid dot, no blur
        ctx.beginPath();
        ctx.arc(x, y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(148,163,184,0.75)';
        ctx.fill();
        return;
      }

      // Determine circle color based on priority
      let circleColor = '#10b981'; // Default green (all completed)
      
      if (cityData.naoAtribuida > 0) {
        circleColor = '#2563eb'; // Blue - highest priority
      } else if (cityData.naoIniciada > 0) {
        circleColor = '#ef4444'; // Red - second priority
      } else if (cityData.emAndamento > 0) {
        circleColor = '#f59e0b'; // Yellow - third priority
      }

      // Circle size based on zoom
      const circleRadius = 8 * Math.pow(zs, 0.5);

      // Draw outer glow
      const glowGrad = ctx.createRadialGradient(x, y, 0, x, y, circleRadius * 2);
      glowGrad.addColorStop(0, circleColor + '40');
      glowGrad.addColorStop(0.5, circleColor + '20');
      glowGrad.addColorStop(1, circleColor + '00');
      
      ctx.beginPath();
      ctx.arc(x, y, circleRadius * 2, 0, Math.PI * 2);
      ctx.fillStyle = glowGrad;
      ctx.fill();

      // Draw solid circle
      ctx.beginPath();
      ctx.arc(x, y, circleRadius, 0, Math.PI * 2);
      ctx.fillStyle = circleColor;
      ctx.fill();
      
      // Draw white border
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // ── City labels (appear gradually as zoom increases) ─────────────────────
    const LABEL_START_ZOOM = 1.8;  // zoom level where labels begin to appear
    const LABEL_FULL_ZOOM  = 3.5;  // zoom level where labels are fully visible
    if (zs >= LABEL_START_ZOOM) {
      const labelOpacity = Math.min(1, (zs - LABEL_START_ZOOM) / (LABEL_FULL_ZOOM - LABEL_START_ZOOM));
      const fontSizeCanvas = Math.min(20, 5 * zs); // grows with zoom, capped at 20px
      ctx.font = `600 ${fontSizeCanvas}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.textAlign = 'center';

      // Sort: cities with demands first (so they get label priority), then alphabetical
      const sortedForLabels = [...RS_CITIES].sort((a: any, b: any) => {
        const ca = demandCounts[a.id]?.total ?? 0;
        const cb = demandCounts[b.id]?.total ?? 0;
        return cb - ca;
      });

      const placedRects: { x1: number; y1: number; x2: number; y2: number }[] = [];
      const padding = 2;

      sortedForLabels.forEach((city: any) => {
        if (!city.latitude || !city.longitude) return;
        const { x, y } = geoToCanvas(city.longitude, city.latitude, W, H);
        if (x < -10 || x > W + 10 || y < -10 || y > H + 10) return;

        const label = city.nome as string;
        const tw = ctx.measureText(label).width;
        const th = fontSizeCanvas;
        const lx = x - tw / 2 - padding;
        const ly = y + 6;  // below the dot
        const rx = x + tw / 2 + padding;
        const ry = ly + th + padding;

        // Check overlap with already placed labels
        const overlaps = placedRects.some(r =>
          lx < r.x2 && rx > r.x1 && ly < r.y2 && ry > r.y1
        );
        if (overlaps) return;

        placedRects.push({ x1: lx, y1: ly, x2: rx, y2: ry });

        const cityData = demandCounts[city.id];
        const count = cityData?.total ?? 0;
        const textColor = count > 0
          ? `rgba(30,41,59,${labelOpacity})`
          : `rgba(100,116,139,${labelOpacity * 0.7})`;

        // Subtle white halo behind text for legibility
        ctx.strokeStyle = `rgba(255,255,255,${labelOpacity * 0.85})`;
        ctx.lineWidth = 3;
        ctx.strokeText(label, x, ly + th);
        ctx.fillStyle = textColor;
        ctx.fillText(label, x, ly + th);
      });
    }
  }, [geoToCanvas, demandCounts]);

  // ── Re-draw helpers ──────────────────────────────────────────────────────────
  const requestDraw = useCallback(() => {
    requestAnimationFrame(draw);
  }, [draw]);

  useEffect(() => {
    draw();
    const observer = new ResizeObserver(draw);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [draw]);

  // ── Zoom (wheel) ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const W = rect.width, H = rect.height;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const { scale, offsetX, offsetY } = viewRef.current;
      const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, scale * factor));
      const ratio = newScale / scale;

      const cx = W / 2, cy = H / 2;
      const rawOffsetX = mx - (mx - cx - offsetX) * ratio - cx;
      const rawOffsetY = my - (my - cy - offsetY) * ratio - cy;
      const clamped = clampOffset(rawOffsetX, rawOffsetY, newScale, W, H);

      viewRef.current = { scale: newScale, offsetX: clamped.offsetX, offsetY: clamped.offsetY };
      setIsZoomed(newScale > 1.05 || Math.abs(clamped.offsetX) > 5 || Math.abs(clamped.offsetY) > 5);
      requestDraw();
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [requestDraw]);

  // ── Pan (pointer drag) ───────────────────────────────────────────────────────
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    dragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY };
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragRef.current.active) {
      // Tooltip hover logic
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas || !wrapper) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const W = rect.width, H = rect.height;

      type CityData = { total: number; naoIniciada: number; emAndamento: number; concluida: number; naoAtribuida: number; };
      let found: { name: string; id: number; data: CityData; } | null = null;
      let bestDist = Infinity;
      const zs = viewRef.current.scale;

      RS_CITIES.forEach((city: any) => {
        if (!city.latitude || !city.longitude) return;
        const { x, y } = geoToCanvas(city.longitude, city.latitude, W, H);
        const dist = Math.hypot(mx - x, my - y);
        const cityData = demandCounts[city.id];
        const cnt = cityData?.total ?? 0;
        const zs2 = viewRef.current.scale;
        const r = cnt > 0
          ? (14 + (cnt / visibleMaxRef.current) * 26) * Math.pow(zs2, 0.7) * 0.5
          : 6;
        if (dist <= Math.max(r, 10) && dist < bestDist && cityData) {
          bestDist = dist;
          found = { name: city.nome, id: city.id, data: cityData };
        }
      });

      if (found !== null) {
        const { name, data } = found;
        setTooltip({ x: mx + 12, y: my - 10, visible: true, cityName: name, data });
        canvas.style.cursor = 'pointer';
      } else {
        setTooltip(t => ({ ...t, visible: false }));
        canvas.style.cursor = 'grab';
      }
      return;
    }

    // Panning
    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;
    const canvas2 = canvasRef.current;
    const W2 = canvas2?.getBoundingClientRect().width ?? 400;
    const H2 = canvas2?.getBoundingClientRect().height ?? 600;
    const { scale: sc } = viewRef.current;
    const clamped = clampOffset(
      viewRef.current.offsetX + dx,
      viewRef.current.offsetY + dy,
      sc, W2, H2
    );
    viewRef.current.offsetX = clamped.offsetX;
    viewRef.current.offsetY = clamped.offsetY;
    setIsZoomed(viewRef.current.scale > 1.05 || Math.abs(clamped.offsetX) > 5 || Math.abs(clamped.offsetY) > 5);
    requestDraw();
    const canvas = canvasRef.current;
    if (canvas) canvas.style.cursor = 'grabbing';
  }, [geoToCanvas, demandCounts, requestDraw]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    const canvas = canvasRef.current;
    if (canvas) canvas.style.cursor = 'grab';
  }, []);

  const handlePointerLeave = useCallback(() => {
    dragRef.current.active = false;
    setTooltip(t => ({ ...t, visible: false }));
  }, []);

  const handleClick = useCallback(() => {
    if (!tooltip.visible || !onCityClick) return;
    const city = RS_CITIES.find(c => c.nome === tooltip.cityName);
    if (city) onCityClick(city.id, city.nome);
  }, [tooltip, onCityClick]);

  // Reset zoom on double-click
  const handleDoubleClick = useCallback(() => {
    viewRef.current = { scale: 1, offsetX: 0, offsetY: 0 };
    setIsZoomed(false);
    requestDraw();
  }, [requestDraw]);

  const handleRecenter = useCallback(() => {
    const DURATION = 400; // ms
    const start = performance.now();
    const from = { ...viewRef.current };

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3); // cubic ease-out

    const animate = (now: number) => {
      const elapsed = now - start;
      const t = easeOut(Math.min(elapsed / DURATION, 1));

      viewRef.current = {
        scale:   from.scale   + (1 - from.scale)   * t,
        offsetX: from.offsetX + (0 - from.offsetX) * t,
        offsetY: from.offsetY + (0 - from.offsetY) * t,
      };

      draw();

      if (elapsed < DURATION) {
        requestAnimationFrame(animate);
      } else {
        viewRef.current = { scale: 1, offsetX: 0, offsetY: 0 };
        setIsZoomed(false);
        draw();
      }
    };

    requestAnimationFrame(animate);
  }, [draw]);

  const visibleMax = visibleMaxRef.current;

  return (
    <MapWrapper ref={wrapperRef}>
      <Canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: 'grab' }}
      />

      <TooltipEl $x={tooltip.x} $y={tooltip.y} $visible={tooltip.visible}>
        <TooltipCityName>{tooltip.cityName}</TooltipCityName>
        <TooltipCount>
          {!tooltip.data || tooltip.data.total === 0 ? (
            'Nenhuma demanda'
          ) : (
            <div>
              {tooltip.data.naoAtribuida > 0 && (
                <div style={{ color: 'var(--accent)' }}>
                  {tooltip.data.naoAtribuida} Não Atribuída{tooltip.data.naoAtribuida !== 1 ? 's' : ''}
                </div>
              )}
              {tooltip.data.naoIniciada > 0 && (
                <div style={{ color: '#ef4444' }}>
                  {tooltip.data.naoIniciada} Não Iniciada{tooltip.data.naoIniciada !== 1 ? 's' : ''}
                </div>
              )}
              {tooltip.data.emAndamento > 0 && (
                <div style={{ color: '#f59e0b' }}>
                  {tooltip.data.emAndamento} Em Andamento
                </div>
              )}
              {tooltip.data.concluida > 0 && (
                <div style={{ color: '#10b981' }}>
                  {tooltip.data.concluida} Concluída{tooltip.data.concluida !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}
        </TooltipCount>
      </TooltipEl>

      <RecenterButton $visible={isZoomed} onClick={handleRecenter} title="Recentralizar">
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none"/>
        </svg>
      </RecenterButton>

    </MapWrapper>
  );
};

export default DemandsHeatMap;
